import { useState } from "react";
import { GENRES, REGIONS, SORTS, YEARS, type FilterState, type SortKey } from "@/lib/media";

interface FilterBarProps {
  filter: FilterState;
  resultCount: number;
  onChange: (key: keyof FilterState, value: string) => void;
  onReset: () => void;
}

const GROUPS: { key: keyof FilterState; label: string; options: string[] }[] = [
  { key: "region", label: "地区", options: REGIONS },
  { key: "year", label: "年份", options: YEARS },
  { key: "genre", label: "类型", options: GENRES },
];

export default function FilterBar({ filter, resultCount, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg bg-background-900 border border-background-800 p-4 md:p-5 mb-6">
      <div className="flex items-center justify-between gap-3 mb-0 md:mb-4">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-400"></i>
          <span className="font-heading text-base font-semibold text-foreground-100">筛选</span>
          <span className="text-xs text-foreground-500">共 {resultCount} 部作品</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-md bg-background-800 hover:bg-background-700 text-foreground-300 text-xs whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-refresh-line"></i>
            重置
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden flex items-center gap-1 px-3 py-1.5 rounded-md bg-background-800 text-foreground-200 text-xs whitespace-nowrap cursor-pointer"
          >
            {open ? "收起" : "展开"}
            <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>
        </div>
      </div>

      <div className={`${open ? "block" : "hidden"} md:block mt-4 md:mt-0 space-y-4`}>
        {GROUPS.map((group) => (
          <div key={group.key} className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
            <span className="text-sm text-foreground-500 md:w-14 shrink-0 md:pt-1.5">{group.label}</span>
            <div className="flex flex-wrap gap-2">
              {group.options.map((option) => {
                const active = filter[group.key] === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onChange(group.key, option)}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-800 text-foreground-300 hover:bg-background-700 hover:text-foreground-100"
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <span className="text-sm text-foreground-500 md:w-14 shrink-0">排序</span>
          <div className="inline-flex items-center gap-1 px-1 py-1 rounded-full bg-background-800 border border-background-700 w-fit">
            {SORTS.map((sort) => {
              const active = filter.sort === sort.key;
              return (
                <button
                  key={sort.key}
                  type="button"
                  onClick={() => onChange("sort", sort.key as SortKey)}
                  className={`px-4 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap cursor-pointer transition-colors ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-300 hover:text-foreground-50"
                  }`}
                >
                  {sort.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={onReset}
            className="sm:hidden flex items-center gap-1 px-3 py-1.5 rounded-md bg-background-800 text-foreground-300 text-xs whitespace-nowrap cursor-pointer w-fit"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}